/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { dedupeBy } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Search, MessageCircle } from 'lucide-react';
import { CATEGORY_META, initials } from '@/lib/utils-cat';
import { EmptyState } from '@/components/UIExtras';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

const DirectoryPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoize the category chip list — CATEGORY_META is a static constant, so this
  // Object.entries + filter never actually changes between renders.
  const categoryChips = useMemo(
    () => Object.entries(CATEGORY_META).filter(([key]) => key !== 'paziente'),
    []
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (cat) params.category = cat;
      const res = await api.get('/users', { params });
      // Rete di sicurezza: mai due profili identici (stesso id, o stesso nome + categoria)
      setUsers(dedupeBy(dedupeBy(res.data), (u) => `${(u?.full_name || '').trim().toLowerCase()}|${u?.category || ''}`));
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [q, cat]);

  const startChat = async (otherId) => {
    try {
      const res = await api.post('/chat/private', { other_user_id: otherId });
      navigate(`/chat/${res.data.id}`);
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-semibold tracking-tight">{t('directory')}</h1>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('searchProfessionals')}
          className="pl-9"
          data-testid="directory-search-input"
        />
      </div>

      <div className="flex gap-1 flex-wrap" data-testid="directory-category-filter">
        <button onClick={() => setCat('')} className={`text-xs px-3 py-1.5 rounded-full font-medium ${cat === '' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`} data-testid="directory-filter-all">
          {t('allProfessionals')}
        </button>
        {categoryChips.map(([key, meta]) => (
          <button key={key} onClick={() => setCat(key)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${cat === key ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground'}`} data-testid={`directory-filter-${key}`}>
            {t(meta.label)}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {loading && <div className="text-sm text-muted-foreground">{t('loading')}</div>}
        {!loading && users.length === 0 && (
          <div className="md:col-span-2">
            <EmptyState icon={<Search className="h-6 w-6" />} title={t('noResults')} />
          </div>
        )}
        {users.map(u => {
          const meta = CATEGORY_META[u.category];
          const Icon = meta?.icon;
          const isSelf = user?.id === u.id;
          return (
            <div key={u.id} className="p-3 bg-white border border-border rounded-2xl flex items-center gap-3" data-testid="directory-result-card">
              <Avatar className="h-11 w-11 cursor-pointer" onClick={() => navigate(`/profile/${u.id}`)}>
                {u.avatar_base64 && <AvatarImage src={u.avatar_base64} />}
                <AvatarFallback style={{ backgroundColor: meta?.soft, color: meta?.textOnSoft }}>{initials(u.full_name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/profile/${u.id}`)}>
                <div className="font-semibold text-sm truncate">{u.full_name}</div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  {Icon && <Icon className="h-3 w-3" style={{ color: meta.textOnSoft }} />}
                  <span>{t(meta?.label || '')}</span>
                  {u.specialty && <span>· {u.specialty}</span>}
                </div>
              </div>
              {!isSelf && (
                <Button size="sm" variant="secondary" onClick={() => startChat(u.id)} data-testid="directory-message-button">
                  <MessageCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DirectoryPage;
